import { Injectable } from '@angular/core';
import { UrlHandlingStrategy, UrlTree } from '@angular/router';

/**
 * ReactAwareUrlHandlingStrategy
 *
 * Previne a condição de corrida onde o Angular Router redireciona para /pages/error
 * ao reagir ao mesmo evento `hashchange` que o React Router está processando.
 *
 * Quando o usuário navega para uma rota React (ex: #/react-demo), tanto o React Router
 * quanto o Angular Router recebem o evento `hashchange`. O Angular, ao não encontrar a
 * rota na sua tabela, cairia no `path: '**'` e redirecionaria para /pages/error, alterando
 * a URL antes mesmo do React concluir a navegação. Esta strategy intercepta essa decisão
 * dentro do pipeline do Angular e retorna `false` para rotas que pertencem ao React,
 * fazendo o Angular ignorar completamente a navegação.
 *
 * As rotas React são declaradas em `src/react/react-routes.ts` e registradas em
 * `window.__reactRoutes` como side-effect de módulo.
 *
 * Para migrar uma nova tela para React: adicionar o path em `REACT_ROUTES` em
 * `src/react/react-routes.ts` — o Angular passará a ignorá-la automaticamente.
 */
@Injectable()
export class ReactAwareUrlHandlingStrategy implements UrlHandlingStrategy {
  /**
   * Retorna `false` para rotas pertencentes ao React (presentes em `window.__reactRoutes`),
   * impedindo que o Angular as processe. Retorna `true` para todas as demais rotas.
   *
   * Verifica match exato (ex: `/react-demo`) e prefixo com barra (ex: `/react-demo/sub`).
   */
  shouldProcessUrl(url: UrlTree): boolean {
    // Compara só o path: query string e fragment não fazem parte da identidade
    // de rota do registro React, e comparar a URL serializada completa quebra
    // o match para qualquer rota migrada acessada com query string.
    const urlPath = url.toString().split(/[?#]/)[0];
    const reactRoutes: string[] =
      (window as Window & typeof globalThis & { __reactRoutes?: string[] }).__reactRoutes ?? [];

    return !reactRoutes.some(
      (route) => urlPath === route || urlPath.startsWith(route + '/')
    );
  }

  /**
   * Retorna a `UrlTree` sem modificação.
   */
  extract(url: UrlTree): UrlTree {
    return url;
  }

  /**
   * Retorna a `UrlTree` navegada (`newUrlTree`) para que a localização do
   * Angular Router assente após um redirect.
   *
   * Anteriormente retornava `rawUrl`, o que causava um loop infinito de
   * re-navegação: quando o Angular redirecionava (ex: `''` → `full-layout`
   * → `**` → `/pages/error`), a URL navegada (`newUrlTree`) diferia da URL
   * de entrada (`rawUrl`). Retornar `rawUrl` revertia a localização para a
   * URL original, então o router nunca atingia um estado estável e
   * re-navegava repetidamente, saturando a thread principal e congelando a aba.
   *
   * Como `shouldProcessUrl` já impede o Angular de navegar em rotas
   * pertencentes ao React, `merge` só é chamado para URLs que o Angular
   * legitimamente processa — portanto retornar `newUrlTree` é correto e seguro.
   */
  merge(newUrlTree: UrlTree, rawUrl: UrlTree): UrlTree {
    return newUrlTree;
  }
}
